import type { TestingSchedule, TestingScheduleInput } from '../schemas/testingSchedules'
import { tierFromPlanKey } from './stripe-config'
export const TESTING_AMOUNTS = [
  { name: 'Quick', journeys: 2, budget: 1 },
  { name: 'Basic', journeys: 10, budget: 5 },
  { name: 'Detailed', journeys: 50, budget: 25 },
] as const
export const TESTING_FREQUENCIES = ['hourly', 'daily', 'weekly'] as const
export type TestingFrequency = typeof TESTING_FREQUENCIES[number]
export function testingAmountAvailable(plan: string | null | undefined, frequency: TestingFrequency, journeys: number): boolean {
  const tier = tierFromPlanKey(plan)
  if (['team', 'growth', 'enterprise', 'private-cloud', 'on-prem'].includes(tier)) return true
  if (tier === 'individual') return frequency === 'weekly' ? journeys <= 10 : frequency === 'daily' && journeys <= 2
  return frequency === 'weekly' && journeys <= 2
}

export function testingAmountRequiredPlan(frequency: TestingFrequency, journeys: number): string {
  if (frequency === 'weekly' && journeys <= 2) return 'Free'
  if ((frequency === 'weekly' && journeys <= 10) || (frequency === 'daily' && journeys <= 2)) return 'Starter'
  return 'Pro'
}

export function scheduleTestingAmount(schedule: TestingSchedule): number {
  const journeyBudget = schedule.config.budget - (schedule.config.target.kind === 'exploration' ? 1 : 0)
  return [...TESTING_AMOUNTS].reverse().find(amount => amount.budget <= journeyBudget)?.journeys ?? 2
}

export function testingScheduleChanges(schedules: TestingSchedule[], cadence: TestingFrequency, journeys: number | undefined, focus: string | undefined): { id?: string; schedule: TestingScheduleInput }[] {
  const existing = schedules.filter(schedule => schedule.config.cadence === cadence)
  const primary = existing.find(schedule => schedule.config.enabled) ?? existing[0]
  if (journeys === undefined) return existing.filter(schedule => schedule.config.enabled).map(schedule => ({ id: schedule.id, schedule: { ...schedule.config, enabled: false } }))
  const amount = TESTING_AMOUNTS.find(amount => amount.journeys === journeys)!
  const target: TestingScheduleInput['target'] = focus === undefined && primary ? primary.config.target : {
    kind: 'exploration', prompt: focus?.trim() || 'Explore the application and test its main user journeys for bugs.', source_exploration_id: null,
  }
  const schedule: TestingScheduleInput = {
    name: `${cadence[0]!.toUpperCase()}${cadence.slice(1)} testing`, hour: 21, minute: 0, weekday: 0, month_day: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, monthly_budget: null,
    ...primary?.config, enabled: true, cadence, budget: amount.budget + (target.kind === 'exploration' ? 1 : 0), target,
  }
  return [{ id: primary?.id, schedule }, ...existing.filter(other => other.id !== primary?.id && other.config.enabled).map(other => ({ id: other.id, schedule: { ...other.config, enabled: false } }))]
}

export function defaultOnboardingSchedule(plan: string | null | undefined, focus: string): TestingScheduleInput {
  const tier = tierFromPlanKey(plan)
  const cadence = !tier || tier === 'free' ? 'weekly' : 'daily'
  return testingScheduleChanges([], cadence, 2, focus)[0]!.schedule
}
